<template>
  <MonitorCard :title="'Chat Queue'">
    <template #filter>
      <el-select
        v-model="selectedChannels"
        multiple
        collapse-tags
        placeholder="Channel"
      >
        <el-option-group label="FILTER BY CHANNEL">
          <el-option
            v-for="channel in channelOptions"
            :key="channel.value"
            :label="channel.label"
            :value="channel.value"
          ></el-option>
        </el-option-group>
      </el-select>
    </template>
    <template #content>
      <el-table
        size="mini"
        :data="filteredQueueChats"
        :row-class-name="getRowClassName"
      >
        <el-table-column label="POSITION">
          <template slot-scope="{ $index }">
            {{ $index + 1 }}
          </template>
        </el-table-column>
        <el-table-column label="USER ID">
          <template slot-scope="{ row }">
            {{ getUserIdFromChat(row) }}
          </template>
        </el-table-column>
        <el-table-column label="CHANNEL">
          <template slot-scope="{ row }">
            {{ getChannelFromChat(row) }}
          </template>
        </el-table-column>
        <el-table-column label="SOURCE">
          <template slot-scope="{ row }">
            {{ getDepartmentFromChat(row) }}
          </template>
        </el-table-column>
        <el-table-column label="WAIT TIME" class-name="wait-time">
          <template slot-scope="{ row }">
            <span :class="getWaitTimeClassName(row)">
              {{ getWaitTimeFromChat(row) }}
            </span>
          </template>
        </el-table-column>
      </el-table>
    </template>
  </MonitorCard>
</template>
<script>
import _ from 'lodash';
import moment from 'moment-timezone';
import MonitorCard from './MonitorCard.vue';
import { liveAnalyticsMixin } from '@/mixins/liveAnalytics';

export default {
  mixins: [liveAnalyticsMixin],
  components: {
    MonitorCard,
  },
  props: {
    queueChats: {
      type: Array,
      required: true,
    },
    selectedDepartments: {
      type: Array,
      required: true,
      default: () => [],
    },
    averageWaitTime: {
      type: Number,
      required: true,
      default: 0,
    },
  },
  data() {
    return {
      channelLabels: {
        facebook: 'Facebook',
        line: 'Line',
        telegram: 'Telegram',
        webchat: 'Webchat',
        wechat: 'WeChat',
        whatsapp: 'WhatsApp',
        workplace: 'Workplace',
      },
      selectedChannels: [],
      currentTime: Date.now(),
      exceededAverageChats: new Set(),
    };
  },
  computed: {
    colorThresholds() {
      return _.get(
        this.$store.state.modules,
        'handover.liveChatLiveAnalytics',
        {
          amberThreshold: 30,
          redThreshold: 60,
        },
      );
    },
    channelOptions() {
      const stateVariables = this.queueChats.map((chat) => chat.stateVariables);
      const allChannels = stateVariables
        .map((chat) => chat?.channel)
        .filter(Boolean);

      const channels = Array.from(new Set(allChannels));

      return channels.map((channel) => ({
        label: this.channelLabels[channel] || channel,
        value: channel,
      }));
    },
    filteredQueueChats() {
      let chats = this.queueChats;

      // Filter by channel
      if (this.selectedChannels?.length > 0) {
        chats = chats.filter((chat) =>
          this.selectedChannels.includes(chat.stateVariables?.channel),
        );
      }

      // Sort by date created (oldest first)
      chats = chats.sort((a, b) => {
        const aCreatedAt = moment(a.date_created).valueOf();
        const bCreatedAt = moment(b.date_created).valueOf();

        return aCreatedAt - bCreatedAt;
      });

      return chats;
    },
  },
  watch: {
    queueChats(newChats) {
      // Clean up exceededAverageChats Set when chats are removed
      const currentChatIds = new Set(
        newChats.map((chat) => chat.RowKey || chat.id),
      );
      for (const chatId of this.exceededAverageChats) {
        if (!currentChatIds.has(chatId)) {
          this.exceededAverageChats.delete(chatId);
        }
      }
    },
  },
  methods: {
    getUserIdFromChat(chat) {
      const userId = chat.user_id;
      const channel = this.getChannelFromChat(chat);

      if (!userId) return '-';

      if (channel === this.channelLabels.whatsapp && !userId.startsWith('+')) {
        return `+${userId}`;
      }

      return userId;
    },
    getChannelFromChat(chat) {
      const stateVariables = chat.stateVariables;

      if (!stateVariables?.channel) return '-';

      const channel = stateVariables.channel;

      return this.channelLabels[channel] || channel;
    },
    getDepartmentFromChat(chat) {
      const rawDept = this.getRawDepartmentFromChat(chat);
      return this.getDepartmentLabel(rawDept) || '-';
    },
    getWaitTimeFromChat(chat) {
      const createdAt = moment(chat.date_created).valueOf();
      const waitMs = this.currentTime - createdAt;
      const duration = moment.duration(waitMs);

      const totalMinutes = Math.floor(duration.asMinutes());
      const seconds = duration.seconds();

      const mm = String(totalMinutes).padStart(2, '0');
      const ss = String(seconds).padStart(2, '0');

      return `${mm}:${ss}`;
    },
    getRowClassName({ row }) {
      const chatId = row.RowKey || row.id;

      // Already marked as exceeded - keep it marked
      if (this.exceededAverageChats.has(chatId)) {
        return 'row-exceeds-average';
      }

      if (!this.averageWaitTime) return '';

      const createdAt = moment(row.date_created).valueOf();
      const waitMs = this.currentTime - createdAt;
      const waitS = waitMs / 1000;

      // First time exceeding average - mark it
      if (
        waitMs > this.averageWaitTime &&
        waitS >= this.colorThresholds.redThreshold
      ) {
        this.exceededAverageChats.add(chatId);
        return 'row-exceeds-average';
      }

      return '';
    },
    getWaitTimeClassName(row) {
      if (!this.averageWaitTime) return '';

      const createdAt = moment(row.date_created).valueOf();
      const waitTime = (this.currentTime - createdAt) / 1000;

      if (waitTime >= this.colorThresholds.redThreshold) {
        return 'danger';
      } else if (waitTime >= this.colorThresholds.amberThreshold) {
        return 'warning';
      }

      return '';
    },
  },
  mounted() {
    this.$timeInterval = setInterval(() => {
      this.currentTime = Date.now();
    }, 1000);
  },
  beforeDestroy() {
    if (this.$timeInterval) {
      clearInterval(this.$timeInterval);
    }
  },
};
</script>
<style lang="scss" scoped>
@import '../../../assets/scss/colors.scss';

::v-deep .el-table {
  .el-table__row {
    > td {
      font-weight: 600;
      font-size: 0.85rem;
    }
  }

  .row-exceeds-average > td {
    background-color: rgba($color-danger, 0.15);
  }

  .wait-time {
    color: $color-grey;
    font-weight: 700;

    .warning {
      color: $color-warning;
    }

    .danger {
      color: $color-danger;
    }
  }
}
</style>
